import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

export const useHealthLogs = (memberId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['health-logs', household?.id, memberId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id && !!memberId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('health_logs')
        .select('id, member_id, log_date, symptoms, foods_eaten, notes')
        .eq('household_id', household.id)
        .eq('member_id', memberId)
        .order('log_date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const upsertLog = async ({ log_date, symptoms, foods_eaten, notes, member_id }) => {
    const { error } = await supabase.from('health_logs').upsert(
      { household_id: household.id, member_id: member_id ?? user.id, log_date, symptoms, foods_eaten, notes },
      { onConflict: 'household_id,member_id,log_date' },
    )
    if (error) throw error
    inv()
  }

  const deleteLog = async (id) => {
    const { error } = await supabase.from('health_logs').delete().eq('id', id)
    if (error) throw error
    inv()
  }

  return { ...query, upsertLog, deleteLog }
}

export const useAllLogs = () => {
  const { household } = useAuth()
  const key = ['health-logs-all', household?.id]
  return useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const cutoff = new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10)
      const { data, error } = await supabase
        .from('health_logs')
        .select('member_id, log_date, symptoms, foods_eaten')
        .eq('household_id', household.id)
        .gte('log_date', cutoff)
      if (error) throw error
      return data ?? []
    },
  })
}

export const useAllergies = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['allergies', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('allergies')
        .select('id, member_id, allergen, severity, notes')
        .eq('household_id', household.id)
        .order('allergen')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const addAllergy = async (fields) => {
    const { error } = await supabase
      .from('allergies')
      .insert({ ...fields, household_id: household.id })
    if (error) throw error
    inv()
  }

  const deleteAllergy = async (id) => {
    const { error } = await supabase.from('allergies').delete().eq('id', id)
    if (error) throw error
    inv()
  }

  return { ...query, addAllergy, deleteAllergy }
}
